"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as RechartsPrimitive from "recharts";
import { cn } from "@/lib/utils";

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = {
  light: "",
  dark: ".dark"
} as const;
export type ChartConfig = { [_k in string]: {
  label?: React.ReactNode;
  icon?: React.ComponentType;
} & ({
  color?: string;
  theme?: never;
} | {
  color?: never;
  theme: Record<keyof typeof THEMES, string>;
}) };
interface ChartContextProps {
  config: ChartConfig;
}
const ChartContext = React.createContext<ChartContextProps | null>(null);
function useChart() {
  const context = React.use(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}
function ChartContainer(t0) {
  const $ = _c(27);
  let children;
  let className;
  let config;
  let id;
  let props;
  if ($[0] !== t0) {
    ({
      id,
      className,
      children,
      config,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = config;
    $[4] = id;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    config = $[3];
    id = $[4];
    props = $[5];
  }
  const uniqueId = React.useId();
  let t1;
  if ($[6] !== id || $[7] !== uniqueId) {
    t1 = id || uniqueId.replace(/:/g, "");
    $[6] = id;
    $[7] = uniqueId;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const chartId = `chart-${t1}`;
  let t2;
  if ($[9] !== config) {
    t2 = {
      config
    };
    $[9] = config;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== className) {
    t3 = cn("flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden", className);
    $[11] = className;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== chartId || $[14] !== config) {
    t4 = <ChartStyle id={chartId} config={config} />;
    $[13] = chartId;
    $[14] = config;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== children) {
    t5 = <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>;
    $[16] = children;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== chartId || $[19] !== props || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div data-slot="chart" data-chart={chartId} className={t3} {...props}>{t4}{t5}</div>;
    $[18] = chartId;
    $[19] = props;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t2 || $[25] !== t6) {
    t7 = <ChartContext value={t2}>{t6}</ChartContext>;
    $[24] = t2;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
const ChartStyle = t0 => {
  const $ = _c(8);
  const {
    id,
    config
  } = t0;
  let t1;
  if ($[0] !== config) {
    t1 = Object.entries(config).filter(_temp);
    $[0] = config;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const colorConfig = t1;
  if (!colorConfig.length) {
    return null;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Object.entries(THEMES);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== colorConfig || $[4] !== id) {
    t3 = t2.map(t4 => {
      const [theme, prefix] = t4;
      return `
${prefix} [data-chart=${id}] {
${colorConfig.map(t5 => {
        const [key, itemConfig] = t5;
        const color = itemConfig.theme?.[theme as keyof typeof itemConfig.theme] || itemConfig.color;
        return color ? `  --color-${key}: ${color};` : null;
      }).join("\n")}
}
`;
    });
    $[3] = colorConfig;
    $[4] = id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = t3.join("\n");
  let t5;
  if ($[6] !== t4) {
    t5 = <style dangerouslySetInnerHTML={{
      __html: t4
    }} />;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
const ChartTooltip = RechartsPrimitive.Tooltip;
function ChartTooltipContent(t0) {
  const $ = _c(45);
  const {
    active,
    payload,
    className,
    indicator: t1,
    hideLabel: t2,
    hideIndicator: t3,
    label,
    labelFormatter,
    labelClassName,
    formatter,
    color,
    nameKey,
    labelKey
  } = t0;
  const indicator = t1 === undefined ? "dot" : t1;
  const hideLabel = t2 === undefined ? false : t2;
  const hideIndicator = t3 === undefined ? false : t3;
  const {
    config
  } = useChart();
  let t4;
  bb0: {
    if (hideLabel || !payload?.length) {
      t4 = null;
      break bb0;
    }
    const [item] = payload;
    const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
    let t5;
    if ($[0] !== config || $[1] !== item || $[2] !== key) {
      t5 = getPayloadConfigFromPayload(config, item, key);
      $[0] = config;
      $[1] = item;
      $[2] = key;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    const itemConfig = t5;
    const value = !labelKey && typeof label === "string" ? config[label as keyof typeof config]?.label || label : itemConfig?.label;
    if (labelFormatter) {
      let t6;
      if ($[4] !== labelClassName) {
        t6 = cn("font-medium", labelClassName);
        $[4] = labelClassName;
        $[5] = t6;
      } else {
        t6 = $[5];
      }
      let t7;
      if ($[6] !== labelFormatter || $[7] !== payload || $[8] !== value) {
        t7 = labelFormatter(value, payload);
        $[6] = labelFormatter;
        $[7] = payload;
        $[8] = value;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      let t8;
      if ($[10] !== t6 || $[11] !== t7) {
        t8 = <div className={t6}>{t7}</div>;
        $[10] = t6;
        $[11] = t7;
        $[12] = t8;
      } else {
        t8 = $[12];
      }
      t4 = t8;
      break bb0;
    }
    if (!value) {
      t4 = null;
      break bb0;
    }
    let t6;
    if ($[13] !== labelClassName) {
      t6 = cn("font-medium", labelClassName);
      $[13] = labelClassName;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    let t7;
    if ($[15] !== t6 || $[16] !== value) {
      t7 = <div className={t6}>{value}</div>;
      $[15] = t6;
      $[16] = value;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t4 = t7;
  }
  const tooltipLabel = t4;
  if (!active || !payload?.length) {
    return null;
  }
  const nestLabel = payload.length === 1 && indicator !== "dot";
  let t5;
  if ($[18] !== className) {
    t5 = cn("grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl", className);
    $[18] = className;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = !nestLabel ? tooltipLabel : null;
  let t7;
  if ($[20] !== color || $[21] !== config || $[22] !== formatter || $[23] !== hideIndicator || $[24] !== indicator || $[25] !== nameKey || $[26] !== nestLabel || $[27] !== payload || $[28] !== tooltipLabel) {
    let t8;
    if ($[30] !== color || $[31] !== config || $[32] !== formatter || $[33] !== hideIndicator || $[34] !== indicator || $[35] !== nameKey || $[36] !== nestLabel || $[37] !== tooltipLabel) {
      t8 = (item_1, index) => {
        const key_0 = `${nameKey || item_1.name || item_1.dataKey || "value"}`;
        const itemConfig_0 = getPayloadConfigFromPayload(config, item_1, key_0);
        const indicatorColor = color || item_1.payload.fill || item_1.color;
        return <div key={item_1.dataKey} className={cn("flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground", indicator === "dot" && "items-center")}>{formatter && item_1?.value !== undefined && item_1.name ? formatter(item_1.value, item_1.name, item_1, index, item_1.payload) : <>{itemConfig_0?.icon ? <itemConfig_0.icon /> : !hideIndicator && <div className={cn("shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)", {
              "h-2.5 w-2.5": indicator === "dot",
              "w-1": indicator === "line",
              "w-0 border-[1.5px] border-dashed bg-transparent": indicator === "dashed",
              "my-0.5": nestLabel && indicator === "dashed"
            })} style={{
              "--color-bg": indicatorColor,
              "--color-border": indicatorColor
            } as React.CSSProperties} />}<div className={cn("flex flex-1 justify-between leading-none", nestLabel ? "items-end" : "items-center")}><div className="grid gap-1.5">{nestLabel ? tooltipLabel : null}<span className="text-muted-foreground">{itemConfig_0?.label || item_1.name}</span></div>{item_1.value !== null && item_1.value !== undefined && <span className="font-mono font-medium text-foreground tabular-nums">{item_1.value.toLocaleString()}</span>}</div></>}</div>;
      };
      $[30] = color;
      $[31] = config;
      $[32] = formatter;
      $[33] = hideIndicator;
      $[34] = indicator;
      $[35] = nameKey;
      $[36] = nestLabel;
      $[37] = tooltipLabel;
      $[38] = t8;
    } else {
      t8 = $[38];
    }
    t7 = payload.filter(_temp2).map(t8);
    $[20] = color;
    $[21] = config;
    $[22] = formatter;
    $[23] = hideIndicator;
    $[24] = indicator;
    $[25] = nameKey;
    $[26] = nestLabel;
    $[27] = payload;
    $[28] = tooltipLabel;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[39] !== t7) {
    t8 = <div className="grid gap-1.5">{t7}</div>;
    $[39] = t7;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== t5 || $[42] !== t6 || $[43] !== t8) {
    t9 = <div className={t5}>{t6}{t8}</div>;
    $[41] = t5;
    $[42] = t6;
    $[43] = t8;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  return t9;
}
function _temp2(item_0) {
  return item_0.type !== "none";
}
const ChartLegend = RechartsPrimitive.Legend;
function ChartLegendContent(t0) {
  const $ = _c(15);
  const {
    className,
    hideIcon: t1,
    payload,
    verticalAlign: t2,
    nameKey
  } = t0;
  const hideIcon = t1 === undefined ? false : t1;
  const verticalAlign = t2 === undefined ? "bottom" : t2;
  const {
    config
  } = useChart();
  if (!payload?.length) {
    return null;
  }
  const t3 = verticalAlign === "top" ? "pb-3" : "pt-3";
  let t4;
  if ($[0] !== className || $[1] !== t3) {
    t4 = cn("flex items-center justify-center gap-4", t3, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== config || $[4] !== hideIcon || $[5] !== nameKey || $[6] !== payload) {
    let t6;
    if ($[8] !== config || $[9] !== hideIcon || $[10] !== nameKey) {
      t6 = item_0 => {
        const key = `${nameKey || item_0.dataKey || "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item_0, key);
        return <div key={item_0.value} className={cn("flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground")}>{itemConfig?.icon && !hideIcon ? <itemConfig.icon /> : <div className="h-2 w-2 shrink-0 rounded-[2px]" style={{
            backgroundColor: item_0.color
          }} />}{itemConfig?.label}</div>;
      };
      $[8] = config;
      $[9] = hideIcon;
      $[10] = nameKey;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t5 = payload.filter(_temp3).map(t6);
    $[3] = config;
    $[4] = hideIcon;
    $[5] = nameKey;
    $[6] = payload;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

// Helper to extract item config from a payload.
function _temp3(item) {
  return item.type !== "none";
}
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }
  const payloadPayload = "payload" in payload && typeof payload.payload === "object" && payload.payload !== null ? payload.payload : undefined;
  let configLabelKey: string = key;
  if (key in payload && typeof payload[key as keyof typeof payload] === "string") {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key as keyof typeof payloadPayload] === "string") {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string;
  }
  return configLabelKey in config ? config[configLabelKey] : config[key as keyof typeof config];
}
export { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent, ChartStyle };
function _temp(t0) {
  const [, config_0] = t0;
  return config_0.theme || config_0.color;
}